/**
 * NyCheckout — EV-first checkout flow
 * mode=quick    → all detail sections pre-collapsed (punch headlines only, expand on demand)
 * mode=detailed → all detail sections pre-expanded with full explanations
 *
 * Flow: 1. Charger situation  2. Installation (if buying)  3. Electricity plan + solar  4. Savings + Checkout
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { base44 } from '@/api/base44Client';
import { Button } from '@/components/ui/button';
import {
  Check, ChevronDown, ChevronUp, ArrowRight, ArrowLeft,
  Shield, Zap, Sun, Info, HelpCircle, ExternalLink, Star, Leaf, Receipt
} from 'lucide-react';
import { Car, RefreshCw, PlugZap } from 'lucide-react';
import { mediaImg } from '@/components/imageConstants';
import { trackCheckoutStart, trackFlowStep } from '@/components/analytics/FlowTracker';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { redirectToExternalCheckout, buildPricingData } from '@/components/checkoutRedirect';
import { useChargerCatalog } from '@/components/charger/useChargerCatalog';
import CatalogChargerInfoModal from '@/components/ev/CatalogChargerInfoModal';
import { InstallModal0, InstallModal1, InstallModal2 } from '@/components/InstallationInfoModals';
import L2SavingsPanel from '@/components/loesning2/L2SavingsPanel';
import ReferralKNumberField from '@/components/henvisning/ReferralKNumberField';
import { CACHED_PRICING } from '@/components/loesning2/L2PlanStep';
import ServiceCancellationNote from '@/components/ServiceCancellationNote';
import { FEATURE_SOLAR } from '@/components/featureFlags';
import ChargerSituationInfoPopup from '@/components/redesign/SituationDetails';
import CheckoutSituationOptions from '@/components/redesign/CheckoutSituationOptions';

const SERIF = { fontFamily: '"Playfair Display", Georgia, serif' };
const SANS = { fontFamily: '"DM Sans", "Space Grotesk", sans-serif' };

const SUBSCRIPTIONS = [
  { key: 'enkel_kvartal', code: 1, name: 'enkel kvartal', apiName: 'enkel kvartal', monthly: 18, billing: 'Kvartalsafregning', badge: 'BILLIGST', badgeIcon: <Star className="w-2.5 h-2.5" />, desc: 'Laveste abonnement, afregning hvert kvartal – perfekt til dig der vil spare' },
  { key: 'enkel_strom', code: 2, name: 'enkel strøm', apiName: 'enkel strøm', monthly: 28, billing: 'Månedlig afregning', badge: 'POPULÆR', badgeIcon: <Zap className="w-2.5 h-2.5" />, desc: 'Månedlig a-conto – mulighed for forbrugsafregning (+0,02 kr./kWh)' },
  { key: 'enkel_natur', code: 3, name: 'enkel natur', apiName: 'enkel natur', monthly: 48, billing: 'Månedlig + naturdonation', badge: 'NATUR 🌿', badgeIcon: <Leaf className="w-2.5 h-2.5" />, desc: '20 kr./md. doneres til Den Danske Naturfond – ellers samme som enkel strøm' },
];

const INSTALL_OPTIONS = [
  { key: 'standard', label: 'Ny installation', sub: 'El-aut. installerer alt', price: 3000, modalKey: 'install_2' },
  { key: 'existing', label: 'Tilslut eksisterende', sub: 'Stik klar i garage', price: 1300, modalKey: 'install_1' },
  { key: 'none', label: 'Ingen installation', sub: 'Eget el-firma', price: 0, modalKey: 'install_0' },
];

// ─── Monthly cost example popup (same as ElaftaleProductCardsTransparent) ──
function PriceBreakdownModal({ open, onClose, productName, zone, wantsPostpaid, livePricing, postalCode, totalKwh, onPostalChange }) {
  const sub = { 'enkel kvartal': { monthly: 18, code: '1' }, 'enkel strøm': { monthly: 28, code: '2' }, 'enkel natur': { monthly: 48, code: '3' } }[productName];
  const [localPostal, setLocalPostal] = React.useState(postalCode || '');
  React.useEffect(() => { setLocalPostal(postalCode || ''); }, [postalCode, open]);
  if (!sub) return null;
  const consumption = totalKwh || 8000;
  const cached = CACHED_PRICING[zone] || CACHED_PRICING.DK2;
  const liveProduct = livePricing?.products?.[productName];
  const isLive = !!liveProduct;
  // Use 12mo average spot from live data if available, else cached fallback (matches Elaftale page)
  const spotOreExVat = isLive ? parseFloat(liveProduct.handler?.spotPrice?.ore || 0) : cached.spotPrice;
  const spotOreInclVat = (spotOreExVat * 1.25).toFixed(2);
  const pricingPeriod = liveProduct?._pricingPeriod || '12mo';
  const periodLabel = pricingPeriod === 'now' ? 'aktuel spotpris' : pricingPeriod === '1mo' ? 'seneste måned' : '12 måneder';
  const distTariffInclVat = isLive ? (parseFloat(liveProduct.distribution?.distribution?.ore || 20) * 1.25).toFixed(2) : (cached.distribution * 1.25).toFixed(2);
  const distSubInclVat = isLive ? (parseFloat(liveProduct.distribution?.subscription?.ore || 0) * 1.25).toFixed(2) : (0 * 1.25).toFixed(2);
  const distTotalInclVat = isLive ? (parseFloat(liveProduct.distribution?.total?.ore || 20) * 1.25).toFixed(2) : (parseFloat(distTariffInclVat) + parseFloat(distSubInclVat)).toFixed(2);
  const transTariffInclVat = isLive ? (parseFloat(liveProduct.transmission?.tariff?.ore || 11.50) * 1.25).toFixed(2) : (cached.transmission * 0.785).toFixed(2);
  const transSubInclVat = isLive ? (parseFloat(liveProduct.transmission?.subscription?.ore || 3.12) * 1.25).toFixed(2) : (cached.transmission * 0.215).toFixed(2);
  const transTotalInclVat = isLive ? (parseFloat(liveProduct.transmission?.total?.ore || 14.62) * 1.25).toFixed(2) : (cached.transmission * 1.25).toFixed(2);
  const elafgiftInclVat = isLive ? (parseFloat(liveProduct.taxes?.electricityTax?.ore || 0.80) * 1.25).toFixed(2) : (cached.electricityTax * 1.25).toFixed(2);
  const dsoName = livePricing?.distribution?.dso_name || (zone === 'DK2' ? 'Radius Elnet' : 'N1');
  const subOrePerKwh = (sub.monthly / 1.25 * 12 / consumption) * 100 * 1.25;
  // Use live typicalTotal if available for the most accurate total (matches Elaftale page)
  const liveTypicalOre = isLive ? parseFloat(liveProduct.typicalTotal?.ore || 0) : 0;
  const totalOre = liveTypicalOre > 0
    ? liveTypicalOre + (wantsPostpaid ? 2 : 0)
    : parseFloat(spotOreInclVat) + subOrePerKwh + parseFloat(distTotalInclVat) + parseFloat(transTotalInclVat) + parseFloat(elafgiftInclVat) + (wantsPostpaid ? 2 : 0);
  // "Ren elpris til enkel" = pure spot price incl VAT (matches the card rows' "Gns. spotpris")
  const enkelPriceKr = ((parseFloat(spotOreInclVat) + (wantsPostpaid ? 2 : 0)) / 100).toFixed(2);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto bg-white rounded-2xl border border-gray-200" style={SANS}>
        <DialogHeader>
          <DialogTitle className="text-[#2F424D]" style={SERIF}>Detaljeret priseksempel</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 pt-1">
          <div className="bg-[#f5f3ee] border border-[#e8e4d9] rounded-xl p-4">
            <p className="text-sm font-bold text-[#2F424D] mb-1" style={SERIF}>Sådan er prisen bygget op</p>
            <p className="text-xs text-gray-500 mb-2">{consumption.toLocaleString('da-DK')} kWh/år · Priszone: {zone}</p>
            <div className="flex items-center gap-2">
              <input
                type="text" inputMode="numeric" maxLength={4}
                value={localPostal}
                onChange={e => {
                  const v = e.target.value.replace(/\D/g, '').slice(0, 4);
                  setLocalPostal(v);
                  if (v.length === 4) onPostalChange?.(v);
                }}
                placeholder="Postnr."
                className="w-20 px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#2F424D] text-center font-medium bg-white"
              />
              <span className="text-xs text-gray-500">{dsoName || (zone === 'DK2' ? 'Radius Elnet' : 'N1')} · {zone}</span>
            </div>
            {isLive && <p className="text-xs text-[#2F424D] mt-2">✓ Live-priser ({livePricing.date}) — baseret på gennemsnit over {periodLabel}</p>}
          </div>
          <div className="bg-[#D4A62A]/10 border-2 border-[#D4A62A]/30 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <p className="text-sm font-bold text-[#2F424D]" style={SERIF}>Elpris fra Enkel</p>
              <span className="text-2xl font-bold text-[#2F424D]" style={SERIF}>~{enkelPriceKr} kr./kWh</span>
            </div>
            <p className="text-[10px] text-gray-500 mt-1">Gns. spotpris fra Nord Pool{wantsPostpaid ? ' + 2 øre/kWh forbrugsafregning' : ''} — uden tillæg. Hertil kommer net, tariffer, afgifter og moms (vist nedenfor). Denne pris vises på produktkortene.</p>
          </div>
          {[
            { title: `Elhandel – Enkel Energi`, items: [
              ['Spotpris', `${spotOreInclVat} øre/kWh`],
              ['Spotpris tillæg', '0,00 øre/kWh'],
              ['Abonnement', `${sub.monthly.toFixed(2)} kr./md.`],
              ...(wantsPostpaid ? [['Forbrugsafregning', '+2,00 øre/kWh']] : []),
            ]},
            { title: `Dit Netselskab: ${dsoName}`, items: [
              ['Distribution', `${distTariffInclVat} øre/kWh`],
              ['Abonnement', `${distSubInclVat} øre/kWh`],
              ['Samlet', `${distTotalInclVat} øre/kWh`],
            ]},
            { title: 'Energinet', items: [
              ['Tariffer', `${transTariffInclVat} øre/kWh`],
              ['Abonnement', `${transSubInclVat} øre/kWh`],
              ['Samlet', `${transTotalInclVat} øre/kWh`],
            ]},
            { title: 'Beregnet el-afgift', items: [['Beregnet el-afgift', `${elafgiftInclVat} øre/kWh`]] },
          ].map((block, i) => (
            <div key={i} className="border border-gray-200 rounded-xl p-4">
              <p className="text-sm font-bold text-[#2F424D] mb-2" style={SERIF}>{block.title}</p>
              <div className="space-y-1.5">
                {block.items.map(([l, v], j) => (
                  <div key={j} className={`flex justify-between text-xs ${j === block.items.length - 1 && block.items.length > 1 ? 'border-t border-gray-100 pt-1.5 font-bold text-[#2F424D]' : 'text-gray-600'}`}>
                    <span>{l}</span><span>{v}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
          <div className="bg-[#2F424D] rounded-xl p-4">
            <div className="flex items-center justify-between">
              <p className="text-sm font-bold text-white" style={SERIF}>Typisk samlet elpris</p>
              <span className="text-2xl font-bold text-white" style={SERIF}>~{(totalOre / 100).toFixed(2).replace('.', ',')} kr./kWh</span>
            </div>
            <p className="text-[10px] text-white/60 mt-1">Inkl. spotpris, net, tariffer, afgifter, moms og abonnement</p>
          </div>
          <p className="text-[9px] text-gray-400 text-center">Alle priser er inkl. moms (25%). Spotprisen varierer time for time – ovenstående er {isLive && pricingPeriod === 'now' ? 'den aktuelle spotpris lige nu' : isLive && pricingPeriod === '1mo' ? 'et gennemsnit over seneste måned' : 'et gennemsnit over de seneste 12 måneder'}.</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Charger situation info popups ───────────────────────────────────────────
// ─── Solar info popup (Group 6 warning + cancellation notice) ────────────────
function SolarInfoPopup({ open, onClose }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6" onClick={e => e.stopPropagation()} style={SANS}>
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#D4A62A]/20 rounded-xl flex items-center justify-center flex-shrink-0">
              <Sun className="w-6 h-6 text-[#b8860b]" />
            </div>
            <div>
              <h3 className="font-bold text-[#2F424D] text-base" style={SERIF}>Vigtig info om solcelleaftale</h3>
              <p className="text-xs text-gray-400">Læs inden du tilmelder dig</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 ml-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        <div className="space-y-3 mb-5">
          <div className="bg-red-50 border border-red-200 rounded-xl p-3">
            <p className="text-sm font-semibold text-red-800 mb-1">⚠ Er dit anlæg omfattet af aftagepligten?</p>
            <p className="text-xs text-red-700 leading-relaxed">
              Aftagepligten er en ordning, hvor Energinet (via Vindstød) automatisk opkøber din overskudsstrøm til spotpris. Er dit anlæg omfattet – <strong>uanset afregningsgruppe</strong> – kan vi ikke overtage din produktionsaftale, før du har fravalgt dit pristillæg hos Energistyrelsen via Borger.dk (MitID). Det tager ofte op til 3 måneder.
            </p>
          </div>

          <div className="bg-[#f5f3ee] border border-[#e8e4d9] rounded-xl p-3">
            <p className="text-sm font-semibold text-[#2F424D] mb-1">✓ Uden aftagepligt kan vi overtage</p>
            <p className="text-xs text-gray-600 leading-relaxed">
              Er dit anlæg ikke omfattet af aftagepligten, kan vi overtage og afregne dit overskud til spotpris – uden leverandørgebyr, uanset afregningsgruppe. Læs den fulde guide på vores solcelleaftale-side.
            </p>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
            <p className="text-sm font-semibold text-amber-800 mb-1">📅 Opsigelsesvarsel</p>
            <p className="text-xs text-amber-700 leading-relaxed">
              Solcelleaftalen har et <strong>opsigelsesvarsel på løbende måned + 1 måneds varsel</strong>. Det betyder, at opsiger du eksempelvis den 15. januar, ophører aftalen den 28. februar. Alle øvrige el-aftaler hos enkel kan opsiges med 1 dags varsel.
            </p>
          </div>
        </div>

        <a href="/solcelleaftale" target="_blank" rel="noopener noreferrer"
          className="flex items-center justify-center gap-1.5 text-sm font-semibold text-[#2F424D] underline underline-offset-4 hover:text-[#1F2E33] mb-4">
          Læs alt om solcelleaftalen <ExternalLink className="w-3.5 h-3.5" />
        </a>

        <div className="flex gap-3">
          <button onClick={onClose}
            className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl py-2.5 text-sm transition-colors">
            Annuller
          </button>
          <button onClick={() => { onClose('confirm'); }}
            className="flex-1 bg-[#2F424D] hover:bg-[#1F2E33] text-white font-semibold rounded-xl py-2.5 text-sm transition-colors">
            Jeg forstår – tilføj solcelleaftale
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Collapsible step wrapper — "Fortsæt" advances to the next step ───────────
function Section({ title, icon, step, isComplete, defaultOpen, mode, children, badge, subtitle, canSave = true, controlledOpen, onToggle, onSaved, continueLabel = 'Fortsæt' }) {
  const [internalOpen, setInternalOpen] = useState(mode === 'detailed' ? true : (defaultOpen || false));
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = (v) => {
    const next = typeof v === 'function' ? v(open) : v;
    if (isControlled) onToggle?.(next);
    else setInternalOpen(next);
  };
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      <button className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-gray-50 transition-colors"
        onClick={() => setOpen(v => !v)}>
        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 transition-colors ${isComplete ? 'bg-[#2F424D] text-white' : 'bg-gray-200 text-[#21343D]'}`}>
          {isComplete ? <Check className="w-4 h-4" strokeWidth={2.5} /> : step}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {icon && <span className="flex-shrink-0 text-[#2F424D]">{icon}</span>}
            <span className="font-semibold text-gray-900" style={SERIF}>{title}</span>
            {badge && <span className="text-[11px] bg-[#F2E7BF] text-[#6B4E0A] font-bold px-2 py-0.5 rounded-full">{badge}</span>}
          </div>
          {subtitle && <p className="text-sm text-gray-600 mt-0.5">{subtitle}</p>}
        </div>
        {open ? <ChevronUp className="w-5 h-5 text-gray-600 flex-shrink-0" /> : <ChevronDown className="w-5 h-5 text-gray-600 flex-shrink-0" />}
      </button>
      {open && (
        <div className="px-5 pb-5 border-t border-gray-50 pt-4">
          {children}
          {canSave ? (
            <button
              onClick={() => { setOpen(false); onSaved?.(); }}
              className="mt-4 w-full flex items-center justify-center gap-2 bg-[#2F424D] hover:bg-[#1F2E33] text-white text-[15px] font-bold rounded-xl h-12 transition-colors">
              {continueLabel} <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <div className="mt-4 w-full flex items-center justify-center gap-2 bg-gray-200 text-gray-600 text-[15px] font-bold rounded-xl h-12 cursor-not-allowed select-none">
              {continueLabel} <ArrowRight className="w-4 h-4" />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Inline expander for "detailed" mode ────────────────────────────────────
function DetailExpander({ label, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-t border-gray-100 mt-2.5 pt-2.5">
      <button onClick={() => setOpen(v => !v)}
        className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-600 transition-colors">
        <Info className="w-3 h-3" /> {label}
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <div className="mt-2 text-xs text-gray-500 leading-relaxed bg-gray-50 rounded-xl p-3">{children}</div>}
    </div>
  );
}

// ─── Included ribbon ─────────────────────────────────────────────────────────
function IncludedRibbon({ hasCharger }) {
  const items = [
    { icon: <RefreshCw className="w-3.5 h-3.5 text-[#D4A62A]" />, label: 'Saving Mode' },
    { icon: <Zap className="w-3 h-3 text-[#D4A62A]" />, label: '10 øre laderabat' },
    { icon: <Shield className="w-3 h-3 text-[#D4A62A]" />, label: 'Serviceaftale 0 kr.' },
    { icon: <Check className="w-3 h-3 text-[#D4A62A]" />, label: 'Ingen binding' },
  ];
  return (
    <div className="bg-[#2F424D] rounded-2xl px-4 py-3 border border-[#99B1BF]/15">
      <div className="flex flex-wrap gap-1.5 items-center">
        <span className="text-white/50 text-[10px] font-medium">Altid inkluderet:</span>
        {items.map((item, i) => (
          <span key={i} className="inline-flex items-center gap-1 bg-white/5 border border-[#99B1BF]/15 rounded-full px-2.5 py-1 text-[10px] text-white font-medium">
            {item.icon} {item.label}
          </span>
        ))}
      </div>
    </div>
  );
}

// ─── Charger card ─────────────────────────────────────────────────────────
function ChargerCard({ charger, selected, onClick, mode }) {
  const [infoOpen, setInfoOpen] = useState(false);
  const isSoldOut = charger.status === 'sold_out';
  const isComingSoon = charger.status === 'coming_soon';
  const disabled = isSoldOut || isComingSoon;
  const price = charger.price || 0;
  const origPrice = charger.original_price || price;
  const hasDiscount = origPrice > price;

  return (
    <div onClick={disabled ? undefined : onClick}
      className={`relative rounded-xl border p-3 transition-all ${disabled ? 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed' : selected ? 'border-[#2F424D]/40 bg-[#F6F3EC] ring-2 ring-[#2F424D]/15 cursor-pointer' : 'border-gray-200 bg-white hover:border-gray-300 cursor-pointer'}`}
      role="button">
      <div className="flex items-start gap-3">
      <div className="w-12 h-12 bg-[#314A56] rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
          <img src={mediaImg(charger.image, 320)} alt={charger.name} loading="lazy" decoding="async" className={`w-full h-full object-contain ${charger.image_scale || ''}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-base font-bold text-[#21343D]" style={SERIF}>{charger.name}</p>
            {isSoldOut && <span className="text-[11px] bg-red-100 text-red-700 font-bold px-2 py-0.5 rounded-full">Udsolgt</span>}
            {isComingSoon && <span className="text-[11px] bg-orange-600 text-white font-bold px-2 py-0.5 rounded-full">Kommer snart</span>}
            {!disabled && charger.badge && <span className="text-[11px] bg-[#F2E7BF] text-[#6B4E0A] font-bold px-2 py-0.5 rounded-full">{charger.badge}</span>}
          </div>
          <div className="flex items-baseline gap-2 mt-0.5 flex-wrap">
            {hasDiscount && <span className="text-xs line-through text-gray-500">{origPrice.toLocaleString('da-DK')} kr.</span>}
            <span className="text-base font-bold text-[#21343D]">{price.toLocaleString('da-DK')} kr.</span>
            {hasDiscount && <span className="text-xs font-bold text-[#7A5A0C]">SPAR {(origPrice - price).toLocaleString('da-DK')} kr.</span>}
          </div>
          {charger.highlighted && hasDiscount && <p className="text-xs text-gray-600 mt-1">så længe lager haves</p>}
          {isSoldOut && <p className="text-xs text-orange-700 font-medium mt-1">⏱ Snart på lager · bestil nu, leveres når på lager</p>}
          <div className="flex flex-wrap gap-1.5 mt-1.5">
            {charger.features?.slice(0, 3).map((f, i) => (
              <span key={i} className="text-xs text-gray-700 bg-white border border-gray-300 px-2 py-0.5 rounded-full">{f}</span>
            ))}
          </div>
          <button onClick={e => { e.stopPropagation(); setInfoOpen(true); }}
            className="text-sm text-[#2F424D] font-semibold underline underline-offset-2 hover:text-[#1F2E33] mt-2 flex items-center gap-1">
            <Info className="w-4 h-4" /> Mere info
          </button>
        </div>
        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${selected ? 'border-[#2F424D] bg-[#2F424D]' : 'border-gray-400 bg-white'}`}>
          {selected && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
        </div>
      </div>
      <CatalogChargerInfoModal charger={charger} open={infoOpen} onClose={() => setInfoOpen(false)} />
    </div>
  );
}

// ─── Laderabat summary card (in plan step) ───────────────────────────────
function LaderabatInlineSummary({ evKwh, subMonthly, eligible }) {
  const laderabat = eligible ? Math.round(evKwh * 0.10) : 0;
  const subYearly = subMonthly * 12;
  const net = subYearly - laderabat;
  if (!eligible || evKwh === 0) return null;
  return (
    <div className="bg-[#F6F3EC] border border-[#D4A62A]/25 rounded-xl px-4 py-3 mt-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 bg-[#D4A62A] rounded-full flex items-center justify-center"><Zap className="w-3 h-3 text-gray-900" /></div>
          <span className="text-sm font-bold text-[#2F424D]" style={SERIF}>Laderabat</span>
        </div>
        <span className="text-base font-bold text-[#D4A62A]" style={SERIF}>-{laderabat} kr./år</span>
      </div>
      <div className="flex items-center justify-between text-xs pl-8 mt-0.5">
        <span className="text-gray-500">10 øre/kWh × {evKwh.toLocaleString('da-DK')} kWh</span>
        <span className="text-[#2F424D] font-semibold">Eff. abn.: {net > 0 ? net : 0} kr./år</span>
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────
export default function NyCheckout() {
  const navigate = useNavigate();
  const { chargers: rawChargers, byId: rawChargersById } = useChargerCatalog();
  const CHARGERS = rawChargers;
  const chargersById = rawChargersById;
  const urlParams = new URLSearchParams(window.location.search);
  const mode = urlParams.get('mode') || 'quick';

  // Danish checkout — ensure the external redirect carries lang=da (reset any leftover EN flag)
  useEffect(() => { try { localStorage.setItem('enkel-lang', 'da'); } catch {} }, []);

  // ─── Funnel analytics: entering the checkout ───
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    trackCheckoutStart({
      mode: p.get('mode') || 'quick',
      plan: p.get('plan') || '',
      situation: p.get('situation') || '',
      charger: p.get('charger') || '',
      entry_referrer: document.referrer || '',
    });
    try { sessionStorage.setItem('checkout_last_step', 'situation'); } catch {}
  }, []);

  // Records a completed checkout step and remembers it for drop-off reporting.
  const completeStep = useCallback((stepName, stepNumber, metadata = {}) => {
    try { sessionStorage.setItem('checkout_last_step', stepName); } catch {}
    trackFlowStep('ny_checkout', stepName, stepNumber, metadata);
  }, []);

  // ─── Pre-selected plan from elaftale page ───
  // plan=1 → kvartal, plan=2 → strøm, plan=3 → natur
  const preselectedPlanCode = urlParams.get('plan') || null;
  const preselectedPlanKey = preselectedPlanCode === '1' ? 'enkel_kvartal' : preselectedPlanCode === '3' ? 'enkel_natur' : preselectedPlanCode === '2' ? 'enkel_strom' : null;
  const planPreselected = !!preselectedPlanKey;
  // solar=1 → solar addon pre-toggled + plan section opened (from /solcelleaftale CTA)
  // Ignored entirely while FEATURE_SOLAR is off.
  const solarPreselected = FEATURE_SOLAR && urlParams.get('solar') === '1';

  // ─── State ───
  const initialSituation = urlParams.get('situation') || null;
  const hasExplicitCharger = urlParams.has('charger');
  const requestedChargerId = urlParams.get('charger') || null;
  const initialInstallation = urlParams.get('installation') || null;
  const initialPostal = urlParams.get('postal') || null;
  const [chargerSituation, setChargerSituation] = useState(initialSituation || (hasExplicitCharger ? 'buy' : null));
  const [selectedChargerId, setSelectedChargerId] = useState(requestedChargerId);
  const [showAllChargers, setShowAllChargers] = useState(false);

  // Default/recommended charger = the highlighted active one (falls back to first active)
  const defaultCharger = CHARGERS.find(c => c.status === 'active' && c.highlighted) || CHARGERS.find(c => c.status === 'active') || CHARGERS[0];
  const defaultChargerId = defaultCharger?.id || null;

  // Once catalog loads, resolve a sensible selected charger: keep the requested one only
  // if it's active; otherwise fall back to the recommended default.
  useEffect(() => {
    if (!CHARGERS.length) return;
    setSelectedChargerId(prev => {
      const cur = prev ? chargersById[prev] : null;
      if (cur && cur.status === 'active') return prev;
      return defaultChargerId;
    });
    setShowAllChargers(prev => prev || (requestedChargerId && requestedChargerId !== defaultChargerId));
  }, [CHARGERS.length]);
  const [installationChoice, setInstallationChoice] = useState(initialInstallation);
  const [installModal, setInstallModal] = useState(null);
  const [selectedPlanKey, setSelectedPlanKey] = useState(preselectedPlanKey || 'enkel_strom');
  const [wantsPostpaid, setWantsPostpaid] = useState(urlParams.get('subscription_f') === '1');
  const [hasSolar, setHasSolar] = useState(solarPreselected);
  const [zone, setZone] = useState('DK2');
  const [postalCode, setPostalCode] = useState(initialPostal || '2450');
  const [dsoName, setDsoName] = useState(null);
  const [livePricingProducts, setLivePricingProducts] = useState(null);
  const [livePricingRaw, setLivePricingRaw] = useState(null);
  const [cache, setCache] = useState({});
  const [cacheLoading, setCacheLoading] = useState(true);
  const [selectedCompetitorSlug, setSelectedCompetitorSlug] = useState('andel-energi');
  const [showPriceModal, setShowPriceModal] = useState(false);
  const [priceModalPlan, setPriceModalPlan] = useState(null);
  const [showSolarPopup, setShowSolarPopup] = useState(false);
  const [chargerInfoType, setChargerInfoType] = useState(null);
  // One step expanded at a time; "Fortsæt" auto-advances to the next one.
  const [openStep, setOpenStep] = useState(
    (initialSituation || hasExplicitCharger)
      ? (((initialSituation === 'buy' || hasExplicitCharger) && !initialInstallation) ? 'charger_pick' : 'plan')
      : 'situation'
  );

  const LADERABAT_KR = 0.10;
  const SMART_CHARGE_FACTOR = 0.22;
  // Fixed consumption defaults — no user-adjustable widget in checkout
  const BASE_KWH = 8000;
  const EV_KWH = 3960; // 20,000 km × 0.22 kWh/km × 0.90 home charge ratio
  const needsCharger = chargerSituation === 'buy';
  const hasCompatibleCharger = chargerSituation === 'have_compatible';
  const serviceAgreement = chargerSituation === 'buy' || chargerSituation === 'have_compatible' || chargerSituation === 'have_other';
  const hasEV = chargerSituation !== null && chargerSituation !== 'no_ev';
  const eligible = (chargerSituation === 'buy' || chargerSituation === 'have_compatible') && hasEV;
  const evKwh = hasEV ? EV_KWH : 0;
  const totalKwh = Math.max(BASE_KWH + evKwh, 1000);
  const getTotalKwh = () => totalKwh;
  const toBucket = (kwh) => Math.round(kwh / 500) * 500;
  const bucket = toBucket(totalKwh);
  const currentSub = SUBSCRIPTIONS.find(s => s.key === selectedPlanKey) || SUBSCRIPTIONS[1];
  const isPostpay = wantsPostpaid && currentSub.key !== 'enkel_kvartal';
  const laderabat = eligible ? Math.round(evKwh * LADERABAT_KR) : 0;
  const smartCharge = eligible ? Math.round(evKwh * SMART_CHARGE_FACTOR) : 0;
  const solarSav = hasSolar ? 180 : 0;
  const subCode = currentSub.code;

  // Auto-scroll to plan section when arriving with pre-set situation
  useEffect(() => {
    if (initialSituation) {
      setTimeout(() => {
        const el = document.getElementById('plan-section');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 400);
    }
  }, []);

  const step1Done = chargerSituation !== null;
  const step2Done = step1Done && (!needsCharger || (selectedChargerId && installationChoice));

  const step3Done = step2Done && !!selectedPlanKey;

  // Fetch live pricing
  const fetchPricing = useCallback((pc) => {
    base44.functions.invoke('getDailyPricing', { consumption: getTotalKwh(), postalCode: pc || postalCode || '2450' })
      .then(res => {
        if (res.data?.success) {
          if (res.data.zone && ['DK1', 'DK2'].includes(res.data.zone)) setZone(res.data.zone);
          if (res.data.distribution?.dso_name) setDsoName(res.data.distribution.dso_name);
          setLivePricingProducts(res.data.products);
          setLivePricingRaw(res.data);
        }
      }).catch(() => {});
  }, []);

  useEffect(() => { fetchPricing(postalCode || '2450'); }, []);

  useEffect(() => {
    setCacheLoading(true);
    base44.entities.ComparativeSavingsSnapshot.filter({ zone, consumption_kwh_bucket: { $gte: 500, $lte: 50000 } }, '-date', 200)
      .then(snaps => { const m = {}; snaps.forEach(s => { if (s.consumption_kwh_bucket && !m[s.consumption_kwh_bucket]) m[s.consumption_kwh_bucket] = s; }); setCache(m); })
      .finally(() => setCacheLoading(false));
  }, [zone]);

  const calcSavingsForSlug = useCallback((slug) => {
    const postpaidYearlyCost = isPostpay ? Math.round(totalKwh * 0.02) : 0;
    const calc = (snap) => {
      if (!snap) return null;
      const ep = snap.enkel_products?.find(p => p.code === subCode);
      const comp = (snap.competitors || []).find(c => c.slug === slug);
      if (!ep || !comp) return null;
      const d = comp.yearly_cost - ep.yearly_cost - postpaidYearlyCost;
      return d > 0 ? Math.round(d) : 0;
    };
    if (cache[bucket]) return calc(cache[bucket]);
    const bkts = Object.keys(cache).map(Number).sort((a, b) => a - b);
    if (!bkts.length) return null;
    let lo = null, hi = null;
    for (const b of bkts) { if (b <= bucket) lo = b; if (b >= bucket && hi === null) hi = b; }
    if (lo && hi && lo !== hi) { const l = calc(cache[lo]); const u = calc(cache[hi]); if (l !== null && u !== null) return Math.round(l + (u - l) * ((bucket - lo) / (hi - lo))); }
    return calc(cache[lo ?? hi]);
  }, [cache, bucket, subCode, isPostpay, totalKwh]);

  const { competitors, competitorName, compElSavings, paymentFeeSavings } = useMemo(() => {
    let snap = cache[bucket];
    if (!snap) { const bkts = Object.keys(cache).map(Number).sort((a, b) => a - b); const nearest = bkts.reduce((best, b) => (!best || Math.abs(b - bucket) < Math.abs(best - bucket)) ? b : best, null); if (nearest !== null) snap = cache[nearest]; }
    const list = (snap?.competitors || []).filter(c => c.yearly_cost > 0 && !/enkel/i.test(c.slug)).sort((a, b) => (a.name || '').localeCompare(b.name || '', 'da'));
    const selected = (selectedCompetitorSlug && list.find(c => c.slug === selectedCompetitorSlug)) || list.find(c => c.slug === 'andel-energi') || list[0];
    const name = selected?.name || 'Andel Energi';
    const savings = selected ? calcSavingsForSlug(selected.slug) : 0;
    const feeSavings = Math.round((selected?.fees_monthly || 0) * 12);
    return { competitors: list, competitorName: name, compElSavings: savings || 0, paymentFeeSavings: feeSavings };
  }, [cache, bucket, selectedCompetitorSlug, calcSavingsForSlug]);

  const handleCheckout = () => {
    const apiName = currentSub.apiName;
    const liveProduct = livePricingProducts?.[apiName];
    const hasLive = !!liveProduct;
    const fallbackSpot = CACHED_PRICING[zone] || CACHED_PRICING.DK2;
    const spotExVat = hasLive ? parseFloat(liveProduct.handler?.spotPrice?.ore || 0) : fallbackSpot.spotPrice;
    const spotInclVat = spotExVat * 1.25;
    const distTariffExVat = hasLive ? parseFloat(liveProduct.distribution?.distribution?.ore || 20) : 34.17;
    const distSubExVat = hasLive ? parseFloat(liveProduct.distribution?.subscription?.ore || 5) : 7.35;
    const transTariffExVat = hasLive ? parseFloat(liveProduct.transmission?.tariff?.ore || 11.50) : 11.50;
    const transSubExVat = hasLive ? parseFloat(liveProduct.transmission?.subscription?.ore || 3.12) : 3.12;
    const elafgiftExVat = hasLive ? parseFloat(liveProduct.taxes?.electricityTax?.ore || 0.80) : fallbackSpot.electricityTax;
    const subMonthlyExVat = currentSub.monthly / 1.25;
    const subOrePerKwh = (subMonthlyExVat * 12 / totalKwh) * 100;
    const forbrugSurchargeExVat = isPostpay ? 1.6 : 0;
    const totalExVat = spotExVat + subOrePerKwh + forbrugSurchargeExVat + (distTariffExVat + distSubExVat) + (transTariffExVat + transSubExVat) + elafgiftExVat;
    const liveTypicalKr = hasLive ? liveProduct.typicalTotal?.kr : null;
    const calculatedPricePerKwh = liveTypicalKr || ((totalExVat * 1.25) / 100).toFixed(2);
    const pricing = buildPricingData({
      consumption: totalKwh, zone, pricePerKwh: calculatedPricePerKwh,
      spotOreInclVat: spotInclVat.toFixed(2), spotOreExVat: spotExVat.toFixed(2),
      subscriptionMonthlyKr: currentSub.monthly, forbrugEnabled: isPostpay, forbrugSurchargeOre: isPostpay ? '2.00' : '0.00',
      distTariffOreInclVat: (distTariffExVat * 1.25).toFixed(2), distSubOreInclVat: (distSubExVat * 1.25).toFixed(2),
      distTotalOreInclVat: ((distTariffExVat + distSubExVat) * 1.25).toFixed(2),
      transTariffOreInclVat: (transTariffExVat * 1.25).toFixed(2), transSubOreInclVat: (transSubExVat * 1.25).toFixed(2),
      transTotalOreInclVat: ((transTariffExVat + transSubExVat) * 1.25).toFixed(2),
      elafgiftOreInclVat: (elafgiftExVat * 1.25).toFixed(2),
      stategridOreInclVat: ((distTariffExVat + distSubExVat + transTariffExVat + transSubExVat + elafgiftExVat) * 1.25).toFixed(2),
      dsoName: dsoName || (zone === 'DK2' ? 'Radius Elnet' : 'N1'),
    });
    // Only orderable (active) chargers go to checkout; fall back to recommended default otherwise.
    const selCh = selectedChargerId ? chargersById[selectedChargerId] : null;
    const effectiveCh = (selCh && selCh.status === 'active') ? selCh : defaultCharger;
    const productCode = needsCharger && effectiveCh ? (effectiveCh.productCode || '0') : '0';
    const installCode = needsCharger ? (installationChoice === 'standard' ? 2 : installationChoice === 'existing' ? 1 : 0) : 0;
    redirectToExternalCheckout({
      product: productCode, installation: installCode,
      subscription_code: currentSub.code, subscription_f: isPostpay ? 1 : 0,
      subscription_solar: hasSolar ? 1 : 0,
      serviceagreement: (chargerSituation === 'buy' || chargerSituation === 'have_compatible') ? 1 : 0,
      chargerSituation,
      pricing,
    });
  };

  return (
    <div className="ee-site ee-checkout min-h-screen" style={SANS}>
      <div className="container mx-auto px-4 sm:px-5 py-5 max-w-2xl">
        <div className="mb-7"><p className="ee-eyebrow">Din bestilling</p><h1 className="text-3xl text-ee-ink font-heading">Vælg din løsning til hjemmet</h1><p className="ee-note mt-3">Dine valg samles i en ordreoversigt. Derefter fortsætter du til adresse, startdato og MitID.</p></div>
        <div className="space-y-3">

          {/* ─── STEP 1: Charger situation ─── */}
          <Section step="1" title="Hvad passer til din situation?" mode={mode}
            icon={<Car className="w-4 h-4 text-[#1a3a32]" />}
            isComplete={step1Done}
            controlledOpen={openStep === 'situation'}
            onToggle={(o) => setOpenStep(o ? 'situation' : null)}
            onSaved={() => {
              completeStep('situation', 1, { charger_situation: chargerSituation });
              setOpenStep(chargerSituation === 'buy' ? 'charger_pick' : 'plan');
            }}
            canSave={step1Done}
            badge={!step1Done ? 'Start her' : undefined}
            subtitle={step1Done ? (
              chargerSituation === 'buy' ? 'Køber ny ladeboks' :
              chargerSituation === 'have_compatible' ? 'Har kompatibel ladeboks' :
              chargerSituation === 'have_other' ? 'Anden ladeboks' :
              'Kun strøm til hjemmet'
            ) : 'Vælg din situation'}>
            <p className="text-sm text-ee-muted mb-4">El-aftalen gælder hele dit hjem. Vælg, om du har en ladeboks, ønsker at købe en eller kun vil bestille strøm.</p>
            <CheckoutSituationOptions value={chargerSituation} onChange={key => { setChargerSituation(key); setInstallationChoice(null); }} onInfo={setChargerInfoType} />

            {/* Status message based on selection */}
            {chargerSituation === 'have_compatible' && (
               <div className="mt-3 rounded-xl px-4 py-3 border bg-[#F6F3EC] border-[#2F424D]/20">
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-[#2F424D] flex-shrink-0" />
                  <p className="text-sm font-semibold text-[#2F424D]">
                    Saving Mode, laderabat og serviceaftale aktiveres automatisk – 0 kr./md.
                  </p>
                </div>
                <p className="text-xs text-gray-600 mt-1 pl-6">Laderabat: 10 øre/kWh fratrækkes på din regning for hjemmeopladning styret af Saving Mode i enkel-appen (kræver kompatibel ladeboks og bil – manuel opladning giver ikke rabat). Andre opkræver 39–69 kr./md. for tilsvarende.</p>
                <ServiceCancellationNote />
              </div>
            )}
            {chargerSituation === 'buy' && (
               <div className="mt-3 rounded-xl px-4 py-3 border bg-[#F6F3EC] border-[#2F424D]/20">
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-[#2F424D] flex-shrink-0" />
                  <p className="text-sm font-semibold text-[#2F424D]">
                    Saving Mode, laderabat og serviceaftale inkluderet – 0 kr./md. Vi trækker fra, ikke til.
                  </p>
                </div>
              </div>
            )}

          </Section>

          {/* ─── STEP 2: Choose charger + installation ─── */}
          {chargerSituation === 'buy' && (
            <Section step="2" title="Vælg ladeboks + installation" mode={mode}
              icon={<PlugZap className="w-4 h-4 text-[#1a3a32]" />}
              isComplete={!!(selectedChargerId && installationChoice)}
              controlledOpen={openStep === 'charger_pick'}
              onToggle={(o) => setOpenStep(o ? 'charger_pick' : null)}
              onSaved={() => {
                completeStep('charger_and_installation', 2, { charger: selectedChargerId, installation: installationChoice });
                setOpenStep('plan');
              }}
              canSave={!!installationChoice}
              subtitle={selectedChargerId && installationChoice ? `${CHARGERS.find(c => c.id === selectedChargerId)?.name || ''} · ${INSTALL_OPTIONS.find(o => o.key === installationChoice)?.label || ''}` : 'Vælg model og installation'}>

              {defaultCharger && (
                <div className="mb-4">
                  <p className="text-xs text-gray-600 uppercase tracking-wider font-bold mb-2">Anbefalet</p>
                  <ChargerCard charger={defaultCharger}
                    selected={selectedChargerId === defaultChargerId && !showAllChargers}
                    onClick={() => { setSelectedChargerId(defaultChargerId); setShowAllChargers(false); }}
                    mode={mode} />
                </div>
              )}

              {!showAllChargers ? (
                <button onClick={() => setShowAllChargers(true)}
                  className="text-sm text-[#2F424D] font-semibold hover:underline flex items-center gap-1 mb-4">
                  <ChevronDown className="w-4 h-4" /> Se alle ladebokse
                </button>
              ) : (
                <div className="mb-4 space-y-2">
                  <div className="flex justify-between items-center mb-3">
                    <p className="text-xs text-gray-600 uppercase tracking-wider font-bold">Alle ladebokse</p>
                    <button onClick={() => setShowAllChargers(false)} className="text-sm font-semibold text-[#2F424D] hover:underline">Luk</button>
                  </div>
                  {CHARGERS.filter(c => c.id !== defaultChargerId).map(charger => (
                    <ChargerCard key={charger.id} charger={charger} selected={selectedChargerId === charger.id}
                      onClick={() => setSelectedChargerId(charger.id)} mode={mode} />
                  ))}
                </div>
              )}

              {/* Installation */}
              <div>
                <p className="text-xs text-gray-600 uppercase tracking-wider font-bold mb-2">Installationstype</p>
                {!installationChoice && <p className="text-sm text-[#7A5A0C] font-semibold mb-2">⚠ Vælg installationstype for at fortsætte</p>}
                <div className="grid grid-cols-3 gap-2 mb-3">
                  {INSTALL_OPTIONS.map(opt => (
                    <button key={opt.key} onClick={() => setInstallationChoice(opt.key)}
                      className={`flex flex-col items-center py-3 px-1.5 rounded-xl border-2 text-center transition-all ${installationChoice === opt.key ? 'bg-[#F6F3EC] border-[#2F424D] text-[#21343D]' : 'bg-white border-gray-200 text-[#21343D] hover:border-gray-400'}`}>
                      <span className="text-sm font-bold leading-tight">{opt.label}</span>
                      <span className="text-xs text-gray-600 mt-0.5">{opt.sub}</span>
                      <span className="text-sm font-bold mt-1 text-[#21343D]">{opt.price > 0 ? `${opt.price.toLocaleString('da-DK')} kr.` : '0 kr.'}</span>
                      <span role="button" tabIndex={0} aria-label={`Læs mere om ${opt.label}`}
                        onClick={e => { e.stopPropagation(); setInstallModal(opt.modalKey); }}
                        className="mt-2 inline-flex items-center gap-1 rounded-full bg-[#2F424D]/10 hover:bg-[#2F424D]/20 text-[#2F424D] px-2.5 py-1 text-xs font-semibold transition-colors">
                        <HelpCircle className="w-4 h-4" /> Læs mere
                      </span>
                    </button>
                  ))}
                </div>
                <div className="bg-[#F6F3EC] border border-[#2F424D]/15 rounded-xl px-3 py-2.5 text-center">
                  <p className="text-sm font-semibold text-[#2F424D]">Du betaler intet i dag · Hele pakken opkræves ved levering</p>
                </div>
              </div>
            </Section>
          )}

          {/* ─── STEP 3: El-aftale ─── */}
          {(step1Done || planPreselected) && (
            <div id="plan-section">
            <Section
              step={needsCharger ? '3' : '2'}
              title="Vælg el-aftale"
              mode={mode}
              icon={<PlugZap className="w-4 h-4 text-[#1a3a32]" />}
              isComplete={!!selectedPlanKey}
              controlledOpen={openStep === 'plan'}
              onToggle={(o) => setOpenStep(o ? 'plan' : null)}
              onSaved={() => {
                completeStep('plan', needsCharger ? 3 : 2, { plan: selectedPlanKey, postpaid: wantsPostpaid, solar: hasSolar });
                setOpenStep(null);
                setTimeout(() => document.getElementById('order-summary')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 120);
              }}
              continueLabel="Se ordreoversigt"
              subtitle={selectedPlanKey ? `${currentSub.name} · ${currentSub.monthly} kr./md. i abn.` : 'Spotpris uden tillæg · Ingen skjulte gebyrer'}
            >
              {/* Plan selection */}
              <div className="space-y-2 mb-4">
                {SUBSCRIPTIONS.map(sub => {
                  const isActive = selectedPlanKey === sub.key;
                  const liveProduct = livePricingProducts?.[sub.apiName];
                  // Compute "ren elpris til enkel" = spot + subscription/kWh (like Elaftale page)
                  const CONSUMPTION_FALLBACK = totalKwh || 8000;
                  const subExVat = sub.monthly / 1.25;
                  const subOrePerKwhForCard = (subExVat * 12 / CONSUMPTION_FALLBACK) * 100 * 1.25;
                  const cached = (CACHED_PRICING[zone] || CACHED_PRICING.DK2);
                  const spotOreExVat = liveProduct ? parseFloat(liveProduct.handler?.spotPrice?.ore || 0) : 0;
                  const spotOreInclVatCard = spotOreExVat > 0 ? spotOreExVat * 1.25 : (cached.spotPrice * 1.25);
                  const postpayExtra = (wantsPostpaid && sub.key !== 'enkel_kvartal') ? 2 : 0;
                  // Pure spotpris (what the electricity itself costs) — incl. postpay surcharge if toggled
                  const spotKrCard = ((spotOreInclVatCard + postpayExtra) / 100).toFixed(2).replace('.', ',');
                  // The shown spot price is an AVERAGE over a period, not the live price
                  const periodCard = liveProduct?._pricingPeriod || '12mo';
                  const spotLabelCard = periodCard === 'now'
                    ? (postpayExtra ? 'Spotpris + 2 øre lige nu' : 'Aktuel spotpris lige nu')
                    : periodCard === '1mo'
                    ? (postpayExtra ? 'Gns. spotpris + 2 øre, seneste måned' : 'Gns. spotpris, seneste måned')
                    : (postpayExtra ? 'Gns. spotpris + 2 øre, 12 mdr.' : 'Gns. spotpris, seneste 12 mdr.');
                  return (
                   <button key={sub.key} onClick={() => { setSelectedPlanKey(sub.key); if (sub.key === 'enkel_kvartal') setWantsPostpaid(false); }}
                    className={`w-full rounded-xl border p-4 text-left transition-all ${isActive ? 'border-[#2F424D]/30 bg-[#F6F3EC]' : 'border-gray-200 bg-white hover:border-gray-300'}`}>
                     <div className="flex items-center justify-between">
                       <div className="flex-1 min-w-0">
                         <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                           <span className="font-bold text-[#21343D] text-base" style={SERIF}>{sub.name}</span>
                           <span className={`inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full ${isActive ? 'bg-[#2F424D] text-white' : 'bg-gray-100 text-gray-600'}`}>{sub.badgeIcon} {sub.badge}</span>
                         </div>
                         <p className="text-sm text-gray-700">{sub.billing}</p>
                         {mode === 'detailed' && <p className="text-xs text-gray-500 mt-0.5">{sub.desc}</p>}
                         <p className="text-xs text-gray-500 mt-1">
                           <span className="font-semibold text-[#2F424D]">{spotLabelCard}</span>: ca. ~{spotKrCard} kr./kWh
                         </p>
                       </div>
                       <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                         <div className="text-right">
                           <span className="text-[11px] font-bold text-[#7A5A0C] uppercase tracking-wide block leading-none mb-0.5">kun</span>
                           <span className="text-3xl font-bold text-[#21343D]" style={SERIF}>{sub.monthly}</span>
                           <span className="text-sm text-gray-700 ml-1">kr./md. <span className="text-xs text-gray-600">i abn.</span></span>
                         </div>
                         <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${isActive ? 'bg-[#2F424D] border-[#2F424D]' : 'border-gray-400 bg-white'}`}>
                           {isActive && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                         </div>
                       </div>
                     </div>
                     <p className="text-xs text-[#2F424D] font-medium mt-1.5">Elpris fra Enkel: spotpris uden tillæg · <span className="text-gray-600 font-normal">net, tariffer, afgifter og moms oveni · ingen binding</span></p>
                     <span role="button" tabIndex={0} onClick={e => { e.stopPropagation(); setPriceModalPlan(sub.apiName); setShowPriceModal(true); }}
                       className="text-sm text-[#2F424D] font-semibold hover:text-[#1F2E33] mt-2 inline-flex items-center gap-1 underline underline-offset-2">
                       <Receipt className="w-4 h-4" /> Se samlet priseksempel
                     </span>
                   </button>
                  );
                })}
              </div>

              {/* Forbrugsafregning toggle */}
              {currentSub.key !== 'enkel_kvartal' && (
                <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 mb-3">
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Forbrugsafregning</p>
                    <p className="text-xs text-gray-600">Betal bagud for faktisk forbrug · +0,02 kr./kWh</p>
                  </div>
                  <button onClick={() => setWantsPostpaid(v => !v)}
                   className={`w-12 h-6 rounded-full transition-colors flex items-center px-1 flex-shrink-0 ${wantsPostpaid ? 'bg-[#2F424D]' : 'bg-gray-300'}`}>
                    <span className={`w-4 h-4 bg-white rounded-full shadow transition-transform ${wantsPostpaid ? 'translate-x-6' : 'translate-x-0'}`} />
                  </button>
                </div>
              )}

              {/* Solar addon – hidden while FEATURE_SOLAR is off */}
              {FEATURE_SOLAR && (
              <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-xl px-4 py-3">
                <div className="flex items-center gap-2">
                  <Sun className="w-5 h-5 text-[#D4A62A]" />
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Solcelleaftale</p>
                    <p className="text-xs text-gray-600">0 kr./md. – uden leverandørgebyr</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {!hasSolar && (
                    <button onClick={() => setShowSolarPopup(true)} className="text-[#2F424D] hover:text-[#1F2E33]" aria-label="Mere information om solcelleaftale">
                      <Info className="w-5 h-5" />
                    </button>
                  )}
                  <button onClick={() => { if (!hasSolar) { setShowSolarPopup(true); } else { setHasSolar(false); } }}
                   className={`w-12 h-6 rounded-full transition-colors flex items-center px-1 ${hasSolar ? 'bg-[#2F424D]' : 'bg-gray-300'}`}>
                    <span className={`w-4 h-4 bg-white rounded-full shadow transition-transform ${hasSolar ? 'translate-x-6' : 'translate-x-0'}`} />
                  </button>
                </div>
              </div>
              )}
            </Section>
            </div>
          )}

          {/* ─── Referral self-report (optional) ─── */}
          {(step2Done || (planPreselected && step1Done)) && (
            <ReferralKNumberField />
          )}

          {/* ─── STEP 4: Savings + Checkout ─── */}
          {(step2Done || (planPreselected && step1Done)) && (
            <L2SavingsPanel
              compElSavings={compElSavings}
              laderabatYearly={chargerSituation === 'have_other' ? 0 : laderabat}
              smartChargeSavingsYearly={chargerSituation === 'have_other' ? 0 : smartCharge}
              solarSavingsYearly={solarSav}
              totalKwh={totalKwh}
              monthlySubscription={currentSub.monthly}
              subName={currentSub.name}
              isEligibleForLaderabat={eligible}
              hasSolar={hasSolar}
              needsCharger={needsCharger}
              selectedCharger={needsCharger ? (selectedChargerId ? chargersById[selectedChargerId] : null) : null}
              loading={cacheLoading}
              onCheckout={handleCheckout}
              hasEV={hasEV}
              installationChoice={installationChoice}
              serviceAgreement={chargerSituation === 'have_other' ? false : serviceAgreement}
              wantsPostpaid={isPostpay}
              competitors={competitors}
              competitorName={competitorName}
              selectedCompetitorSlug={selectedCompetitorSlug}
              onSelectCompetitor={setSelectedCompetitorSlug}
              cacheDate={cache[bucket]?.date}
              paymentFeeSavings={paymentFeeSavings}
            />
          )}

        </div>
      </div>

      {/* Modals */}
      <InstallModal0 open={installModal === 'install_0'} onClose={() => setInstallModal(null)} />
      <InstallModal1 open={installModal === 'install_1'} onClose={() => setInstallModal(null)} />
      <InstallModal2 open={installModal === 'install_2'} onClose={() => setInstallModal(null)} />
      <SolarInfoPopup open={showSolarPopup} onClose={(action) => { setShowSolarPopup(false); if (action === 'confirm') setHasSolar(true); }} />
      <ChargerSituationInfoPopup type={chargerInfoType} onClose={() => setChargerInfoType(null)} />

      <PriceBreakdownModal
        open={showPriceModal && !!priceModalPlan}
        onClose={() => setShowPriceModal(false)}
        productName={priceModalPlan}
        zone={zone}
        wantsPostpaid={isPostpay}
        livePricing={livePricingRaw}
        postalCode={postalCode}
        totalKwh={totalKwh}
        onPostalChange={(pc) => { setPostalCode(pc); fetchPricing(pc); }}
      />
    </div>
  );
}